import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const LibroReclamacionesPage = () => {
    const toast = useRef(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        tipoDocumento: 'DNI',
        numeroDocumento: '',
        nombres: '',
        apellidos: '',
        telefono: '',
        email: '',
        direccion: '',
        esMenorEdad: false,
        apoderadoNombres: '',
        apoderadoApellidos: '',
        apoderadoTipoDocumento: 'DNI',
        apoderadoNumeroDocumento: '',
        tipoBien: 'Producto',
        montoReclamado: '',
        descripcionBien: '',
        tipoReclamacion: 'Reclamo',
        detalleReclamacion: '',
        pedidoConsumidor: '',
        aceptaTerminos: false,
    });

    const [loading, setLoading] = useState(false);
    const [successData, setSuccessData] = useState(null);

    const tipoDocumentoOptions = [
        { label: 'DNI', value: 'DNI' },
        { label: 'CE', value: 'CE' },
        { label: 'Pasaporte', value: 'Pasaporte' },
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const validateForm = () => {
        if (!formData.numeroDocumento || !formData.nombres || !formData.apellidos || !formData.telefono || !formData.email || !formData.direccion) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Por favor complete todos los campos obligatorios del consumidor.', life: 3000 });
            return false;
        }
        if (formData.esMenorEdad && (!formData.apoderadoNombres || !formData.apoderadoApellidos || !formData.apoderadoNumeroDocumento)) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Por favor complete los datos del apoderado.', life: 3000 });
            return false;
        }
        if (!formData.montoReclamado || !formData.descripcionBien) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Por favor complete los datos del bien contratado.', life: 3000 });
            return false;
        }
        if (!formData.detalleReclamacion || !formData.pedidoConsumidor) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Por favor complete el detalle de la reclamación y su pedido.', life: 3000 });
            return false;
        }
        if (!formData.aceptaTerminos) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Debe aceptar los términos y condiciones.', life: 3000 });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);

        try {
            const payload = {
                ...formData,
                datosApoderado: formData.esMenorEdad ? {
                    nombreCompleto: `${formData.apoderadoNombres} ${formData.apoderadoApellidos}`,
                    tipoDocumento: formData.apoderadoTipoDocumento,
                    numeroDocumento: formData.apoderadoNumeroDocumento,
                } : null
            };

            const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://apilw.laptops.pe/api';
            const response = await fetch(`${apiUrl}/reclamaciones`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await response.json();

            if (response.ok) {
                setSuccessData(data);
                setLoading(false);
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: data.message || 'Su reclamación ha sido registrada.', life: 5000 });
            } else if (response.status === 400 && data.errors) {
                setLoading(false);
                data.errors.forEach(err => {
                    toast.current.show({ severity: 'error', summary: 'Error de Validación', detail: err, life: 5000 });
                });
            } else {
                setLoading(false);
                toast.current.show({ severity: 'error', summary: 'Error', detail: data.message || 'Ocurrió un error al enviar el formulario. Intente nuevamente.', life: 5000 });
            }
        } catch (error) {
            setLoading(false);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Ocurrió un error de red. Intente nuevamente.', life: 3000 });
        }
    };

    if (successData) {
        return (
            <div className="flex flex-column min-h-screen">
                <style>{`
                    @media print {
                        .no-print { display: none !important; }
                        body { background-color: white !important; }
                        .surface-ground { background-color: white !important; }
                        .shadow-2 { box-shadow: none !important; }
                        .border-round { border: none !important; }
                    }
                `}</style>
                <div className="no-print">
                    <Header />
                </div>
                <main className="flex-grow-1 flex align-items-center justify-content-center p-4 surface-ground">
                    <div className="surface-card p-6 shadow-2 border-round w-full max-w-3xl text-center">
                        <div className="no-print">
                            <i className="pi pi-check-circle text-green-500" style={{ fontSize: '4rem' }}></i>
                            <h2 className="text-2xl font-bold mt-4 mb-2">Reclamación Registrada con Éxito</h2>
                            <p className="text-color-secondary mb-4">
                                Su {successData.data?.tipoReclamacion?.toLowerCase() || formData.tipoReclamacion.toLowerCase()} ha sido ingresado correctamente a nuestro sistema.
                            </p>
                        </div>

                        <div className="surface-100 p-4 border-round mb-4 text-left printable-area">
                            <h3 className="text-xl font-bold mb-4 text-center border-bottom-1 border-300 pb-2">HOJA DE RECLAMACIÓN</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-sm text-color-secondary mb-2">1. IDENTIFICACIÓN DEL PROVEEDOR</h4>
                                <div className="grid text-sm">
                                    <div className="col-12 md:col-6"><span className="font-semibold">Razón Social:</span> {successData.provider?.razonSocial || 'DIGITAL WINS E.I.R.L.'}</div>
                                    <div className="col-12 md:col-6"><span className="font-semibold">RUC:</span> {successData.provider?.ruc || '20612859630'}</div>
                                    <div className="col-12"><span className="font-semibold">Dirección:</span> {successData.provider?.direccion || 'Av. Ruiseñores 866 Oficina 406 (4to Piso), Santa Anita - Lima - Lima'}</div>
                                </div>
                            </div>

                            <h4 className="font-semibold text-sm text-color-secondary mb-2 border-bottom-1 border-300 pb-1">2. DATOS DEL RECLAMO</h4>
                            <div className="flex justify-content-between mb-2">
                                <span className="font-semibold text-color-secondary">N° Correlativo:</span>
                                <span className="font-bold text-primary">{successData.correlativo}</span>
                            </div>
                            <div className="flex justify-content-between mb-2">
                                <span className="font-semibold text-color-secondary">Fecha de Registro:</span>
                                <span className="font-bold">{successData.data?.fecha_registro ? new Date(successData.data.fecha_registro).toLocaleString('es-PE') : new Date().toLocaleString('es-PE')}</span>
                            </div>
                            <div className="flex justify-content-between mb-2">
                                <span className="font-semibold text-color-secondary">Tipo:</span>
                                <span>{successData.data?.tipoReclamacion || formData.tipoReclamacion}</span>
                            </div>
                            <div className="flex justify-content-between">
                                <span className="font-semibold text-color-secondary">Correo de confirmación:</span>
                                <span>{successData.data?.email || formData.email}</span>
                            </div>
                        </div>

                        <div className="no-print">
                            <p className="text-sm text-color-secondary mb-5">
                                Hemos enviado una copia de este registro a su correo electrónico. Nuestro equipo se pondrá en contacto con usted en el plazo establecido por ley.
                            </p>

                            <div className="flex justify-content-center gap-3">
                                <Button label="Imprimir / Descargar PDF" icon="pi pi-print" className="p-button-outlined" onClick={() => window.print()} />
                                <Button label="Volver al inicio" icon="pi pi-home" onClick={() => navigate('/')} />
                            </div>
                        </div>
                    </div>
                </main>
                <div className="no-print">
                    <Footer />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-column min-h-screen">
            <Helmet>
                <title>Libro de Reclamaciones | Laptops.pe</title>
                <meta name="description" content="Libro de Reclamaciones Virtual de Laptops.pe conforme al Código de Protección y Defensa del Consumidor." />
            </Helmet>
            <Header />
            <Toast ref={toast} />

            <main className="flex-grow-1" style={{ backgroundColor: 'var(--surface-ground)' }}>
                <div className="container mx-auto px-4 py-8 max-w-6xl">
                    <div className="text-center mb-6">
                        <h1 className="text-4xl font-bold mb-2">Libro de Reclamaciones Virtual</h1>
                        <p className="text-color-secondary">Conforme al Código de Protección y Defensa del Consumidor (Ley N° 29571)</p>
                    </div>

                    <div className="surface-card p-5 shadow-2 border-round">
                        {/* Provider Info */}
                        <div className="mb-6 surface-50 p-4 border-round border-1 border-300">
                            <h3 className="text-xl font-semibold mb-3 border-bottom-1 border-300 pb-2">1. Identificación del Proveedor</h3>
                            <div className="grid">
                                <div className="col-12 md:col-6 mb-2">
                                    <span className="font-semibold block text-sm text-color-secondary">Razón Social:</span>
                                    <span>DIGITAL WINS E.I.R.L.</span>
                                </div>
                                <div className="col-12 md:col-6 mb-2">
                                    <span className="font-semibold block text-sm text-color-secondary">RUC:</span>
                                    <span>20612859630</span>
                                </div>
                                <div className="col-12 mb-2">
                                    <span className="font-semibold block text-sm text-color-secondary">Dirección:</span>
                                    <span>Av. Ruiseñores 866 Oficina 406 (4to Piso), Santa Anita - Lima - Lima</span>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* Consumer Info */}
                            <div className="mb-6">
                                <h3 className="text-xl font-semibold mb-4 border-bottom-1 border-300 pb-2">2. Identificación del Consumidor Reclamante</h3>
                                <div className="grid formgrid p-fluid">
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="tipoDocumento">Tipo Doc. *</label>
                                        <Dropdown
                                            id="tipoDocumento"
                                            name="tipoDocumento"
                                            value={formData.tipoDocumento}
                                            options={tipoDocumentoOptions}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="field col-12 md:col-8">
                                        <label htmlFor="numeroDocumento">Número de Documento *</label>
                                        <InputText
                                            id="numeroDocumento"
                                            name="numeroDocumento"
                                            value={formData.numeroDocumento}
                                            onChange={handleChange}
                                            placeholder="Ej. 12345678"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="nombres">Nombres *</label>
                                        <InputText
                                            id="nombres"
                                            name="nombres"
                                            value={formData.nombres}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="apellidos">Apellidos *</label>
                                        <InputText
                                            id="apellidos"
                                            name="apellidos"
                                            value={formData.apellidos}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="telefono">Teléfono *</label>
                                        <InputText
                                            id="telefono"
                                            name="telefono"
                                            value={formData.telefono}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="email">Correo Electrónico *</label>
                                        <InputText
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="field col-12">
                                        <label htmlFor="direccion">Dirección (Av/Calle/Jr - Distrito - Provincia - Depto) *</label>
                                        <InputText
                                            id="direccion"
                                            name="direccion"
                                            value={formData.direccion}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="field col-12 flex align-items-center mt-3">
                                        <Checkbox
                                            inputId="esMenorEdad"
                                            name="esMenorEdad"
                                            checked={formData.esMenorEdad}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="esMenorEdad" className="ml-2 font-semibold">Soy menor de edad (se requieren datos del apoderado)</label>
                                    </div>

                                    {formData.esMenorEdad && (
                                        <div className="col-12 surface-50 p-4 border-round mt-2 grid">
                                            <div className="field col-12 md:col-6">
                                                <label htmlFor="apoderadoNombres">Nombres del Apoderado *</label>
                                                <InputText id="apoderadoNombres" name="apoderadoNombres" value={formData.apoderadoNombres} onChange={handleChange} />
                                            </div>
                                            <div className="field col-12 md:col-6">
                                                <label htmlFor="apoderadoApellidos">Apellidos del Apoderado *</label>
                                                <InputText id="apoderadoApellidos" name="apoderadoApellidos" value={formData.apoderadoApellidos} onChange={handleChange} />
                                            </div>
                                            <div className="field col-12 md:col-4">
                                                <label>Tipo Doc. Apoderado *</label>
                                                <Dropdown name="apoderadoTipoDocumento" value={formData.apoderadoTipoDocumento} options={tipoDocumentoOptions} onChange={handleChange} />
                                            </div>
                                            <div className="field col-12 md:col-8">
                                                <label htmlFor="apoderadoNumeroDocumento">Número de Documento del Apoderado *</label>
                                                <InputText id="apoderadoNumeroDocumento" name="apoderadoNumeroDocumento" value={formData.apoderadoNumeroDocumento} onChange={handleChange} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Product Info */}
                            <div className="mb-6">
                                <h3 className="text-xl font-semibold mb-4 border-bottom-1 border-300 pb-2">3. Identificación del Bien Contratado</h3>
                                <div className="grid formgrid p-fluid">
                                    <div className="field col-12 md:col-6">
                                        <label className="block mb-2">Tipo de Bien *</label>
                                        <div className="flex flex-wrap gap-4">
                                            <div className="flex align-items-center">
                                                <RadioButton inputId="bienProducto" name="tipoBien" value="Producto" onChange={handleChange} checked={formData.tipoBien === 'Producto'} />
                                                <label htmlFor="bienProducto" className="ml-2">Producto</label>
                                            </div>
                                            <div className="flex align-items-center">
                                                <RadioButton inputId="bienServicio" name="tipoBien" value="Servicio" onChange={handleChange} checked={formData.tipoBien === 'Servicio'} />
                                                <label htmlFor="bienServicio" className="ml-2">Servicio</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="montoReclamado">Monto Reclamado (S/) *</label>
                                        <InputText
                                            id="montoReclamado"
                                            name="montoReclamado"
                                            type="number"
                                            step="0.01"
                                            value={formData.montoReclamado}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="field col-12">
                                        <label htmlFor="descripcionBien">Descripción del Producto/Servicio *</label>
                                        <InputTextarea
                                            id="descripcionBien"
                                            name="descripcionBien"
                                            rows={2}
                                            value={formData.descripcionBien}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Claim Details */}
                            <div className="mb-6">
                                <h3 className="text-xl font-semibold mb-4 border-bottom-1 border-300 pb-2">4. Detalle de la Reclamación y Pedido del Consumidor</h3>
                                <div className="grid formgrid p-fluid">
                                    <div className="field col-12">
                                        <label className="block mb-2">Tipo de Reclamación *</label>
                                        <div className="flex flex-wrap gap-4 mb-3">
                                            <div className="flex align-items-center">
                                                <RadioButton inputId="tipoReclamo" name="tipoReclamacion" value="Reclamo" onChange={handleChange} checked={formData.tipoReclamacion === 'Reclamo'} />
                                                <label htmlFor="tipoReclamo" className="ml-2 font-semibold">Reclamo</label>
                                            </div>
                                            <div className="flex align-items-center">
                                                <RadioButton inputId="tipoQueja" name="tipoReclamacion" value="Queja" onChange={handleChange} checked={formData.tipoReclamacion === 'Queja'} />
                                                <label htmlFor="tipoQueja" className="ml-2 font-semibold">Queja</label>
                                            </div>
                                        </div>
                                        <div className="text-sm text-color-secondary p-3 surface-100 border-round mb-3">
                                            <strong>Reclamo:</strong> Disconformidad relacionada a los productos o servicios.<br />
                                            <strong>Queja:</strong> Disconformidad no relacionada a los productos o servicios; o, malestar o descontento respecto a la atención al público.
                                        </div>
                                    </div>
                                    <div className="field col-12">
                                        <label htmlFor="detalleReclamacion">Detalle de la Reclamación *</label>
                                        <InputTextarea
                                            id="detalleReclamacion"
                                            name="detalleReclamacion"
                                            rows={4}
                                            value={formData.detalleReclamacion}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="field col-12">
                                        <label htmlFor="pedidoConsumidor">Pedido (¿Qué solicita?) *</label>
                                        <InputTextarea
                                            id="pedidoConsumidor"
                                            name="pedidoConsumidor"
                                            rows={3}
                                            value={formData.pedidoConsumidor}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Terms */}
                            <div className="mb-5 surface-50 p-4 border-round">
                                <div className="flex align-items-start">
                                    <Checkbox
                                        inputId="aceptaTerminos"
                                        name="aceptaTerminos"
                                        checked={formData.aceptaTerminos}
                                        onChange={handleChange}
                                        className="mt-1"
                                    />
                                    <label htmlFor="aceptaTerminos" className="ml-3 line-height-3 text-sm text-color-secondary">
                                        Declaro que los datos consignados en el presente documento son correctos y se ajustan a la verdad.
                                        Asimismo, autorizo a DIGITAL WINS E.I.R.L. al tratamiento de mis datos personales de acuerdo a su <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Política de Privacidad</a> y me informan que la formulación del reclamo no impide acudir a otras vías de solución de controversias ni es requisito previo para interponer una denuncia ante el INDECOPI.
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-content-end">
                                <Button
                                    label="Enviar Reclamación"
                                    icon="pi pi-send"
                                    type="submit"
                                    loading={loading}
                                    className="p-button-primary px-5 py-3 text-lg"
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default LibroReclamacionesPage;
